<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { api, downloadFile } from '../services/api'

const props = defineProps({ subjects: { type: Array, required: true } })
const emit = defineEmits(['imported'])
const exportSlug = ref('')
const exporting = ref(false)
const file = ref(null)
const enrollStudents = ref(false)
const importStage = ref('')
const preview = ref(null)
const previewArea = ref(null)
const error = ref('')
const message = ref('')
const busy = computed(() => exporting.value || !!importStage.value)
const selectedSubject = computed(() => props.subjects.find(subject => subject.slug === exportSlug.value))
const totals = computed(() => preview.value ? [
  ['Học kỳ', preview.value.counts.semesters], ['Chương / chủ đề', preview.value.counts.chapters],
  ['Bài học', preview.value.counts.lessons], ['Câu hỏi', preview.value.counts.questions], ['Đề', preview.value.counts.quizzes],
] : [])
const changes = computed(() => {
  if (!preview.value) return []
  const stats = preview.value.changes
  return Object.entries({ subject: 'Môn học', semester: 'Học kỳ', chapter: 'Chương / chủ đề', lesson: 'Bài học', question: 'Câu hỏi', quiz: 'Đề', enrollment: 'Đăng ký học' })
    .map(([kind, label]) => ({
      label,
      created: Math.max(0, (stats[kind + '.created'] || 0) - (stats[kind + '.versioned'] || 0)),
      updated: (stats[kind + '.updated'] || 0) + (stats[kind + '.versioned'] || 0),
      unchanged: stats[kind + '.unchanged'] || 0,
    }))
    .filter(row => row.created || row.updated || row.unchanged)
})

watch(() => props.subjects, subjects => {
  if (!subjects.some(subject => subject.slug === exportSlug.value)) exportSlug.value = subjects[0]?.slug || ''
}, { immediate: true })

function invalidatePreview() {
  preview.value = null
  error.value = ''
  message.value = ''
}
watch(enrollStudents, invalidatePreview)

function chooseFile(event) {
  invalidatePreview()
  file.value = event.target.files?.[0] || null
  if (file.value && (!file.value.name.toLowerCase().endsWith('.json') || !file.value.size)) {
    error.value = 'Hãy chọn file học liệu .json có nội dung.'
    file.value = null
  } else if (file.value?.size > 20 * 1024 * 1024) {
    error.value = 'File học liệu vượt quá giới hạn 20 MB.'
    file.value = null
  }
}

function uploadBody(token) {
  const body = new FormData()
  body.append('file', file.value)
  body.append('enroll_students', String(enrollStudents.value))
  if (token) body.append('preview_token', token)
  return body
}

async function exportSubject() {
  if (busy.value || !selectedSubject.value) return
  exporting.value = true
  error.value = ''
  message.value = ''
  const subject = selectedSubject.value
  try {
    const filename = (subject.slug.replace(/[^a-zA-Z0-9._-]/g, '-') || 'subject') + '.json'
    await downloadFile('/admin/content/export/' + encodeURIComponent(subject.slug), filename)
    message.value = 'Đã tải gói học liệu ' + subject.title + '.'
  } catch (err) { error.value = err.message }
  finally { exporting.value = false }
}

async function checkFile() {
  if (busy.value || !file.value) return
  importStage.value = 'preview'
  preview.value = null
  error.value = ''
  message.value = ''
  const chosenFile = file.value
  const chosenEnrollment = enrollStudents.value
  try {
    const result = await api('/admin/content/import/preview', { method: 'POST', body: uploadBody() })
    if (file.value !== chosenFile || enrollStudents.value !== chosenEnrollment) return
    preview.value = result
    await nextTick()
    previewArea.value?.focus()
  } catch (err) { error.value = err.message }
  finally { importStage.value = '' }
}

async function importFile() {
  if (busy.value || !preview.value || !file.value) return
  importStage.value = 'apply'
  error.value = ''
  message.value = ''
  try {
    const result = await api('/admin/content/import', { method: 'POST', body: uploadBody(preview.value.preview_token) })
    preview.value = null
    exportSlug.value = result.subject.slug
    message.value = 'Đã nhập học liệu ' + result.subject.title + ' (' + result.counts.lessons + ' bài). Lịch sử học và điểm đã được giữ nguyên.'
    emit('imported')
  } catch (err) {
    error.value = err.message
    if (err.status === 409) preview.value = null
  } finally { importStage.value = '' }
}
</script>

<template>
  <section class="card content-transfer" aria-label="Xuất / nhập học liệu">
    <div class="transfer-content">
      <div v-if="error" class="error-banner" role="alert">{{ error }}</div>
      <p v-if="message" class="transfer-success" role="status">{{ message }}</p>
      <div class="transfer-grid">
        <form class="transfer-block" @submit.prevent="exportSubject">
          <h3>Xuất một môn</h3><p>Tải học liệu đang có thành file JSON để lưu hoặc đưa sang máy chủ khác.</p>
          <div class="form-group"><label for="export-subject">Môn cần xuất</label><select id="export-subject" v-model="exportSlug" class="form-control" :disabled="busy || !subjects.length" required><option v-if="!subjects.length" value="">Chưa có môn học</option><option v-for="subject in subjects" :key="subject.id" :value="subject.slug">{{ subject.title }} · Lớp {{ subject.grade }}{{ subject.is_published ? '' : ' · Nháp' }}</option></select></div>
          <button class="btn btn-light" :disabled="busy || !exportSlug">{{ exporting ? 'Đang xuất…' : 'Tải file JSON' }}</button>
        </form>
        <form class="transfer-block" @submit.prevent="checkFile">
          <h3>Nhập học liệu</h3><p>Chọn gói của một môn, kiểm tra thay đổi rồi áp dụng. File tối đa 20 MB.</p>
          <div class="form-group"><label for="import-content-file">File học liệu JSON</label><input id="import-content-file" type="file" accept=".json,application/json" :disabled="busy" @change="chooseFile"></div>
          <label class="transfer-enrollment"><input v-model="enrollStudents" type="checkbox" :disabled="busy"><span>Đăng ký môn cho học sinh cùng lớp<small>Chỉ bổ sung đăng ký khi môn đã xuất bản.</small></span></label>
          <button class="btn btn-primary" :disabled="busy || !file">{{ importStage === 'preview' ? 'Đang kiểm tra…' : 'Kiểm tra và xem trước' }}</button>
        </form>
      </div>
      <section v-if="preview" ref="previewArea" class="transfer-preview" tabindex="-1" aria-labelledby="import-preview-title">
        <div class="preview-heading"><div><p class="eyebrow">Xem trước · Chưa lưu thay đổi</p><h3 id="import-preview-title">{{ preview.subject.title }} · Lớp {{ preview.subject.grade }}</h3></div><span class="chip">{{ preview.changes['subject.created'] ? 'Thêm môn mới' : 'Môn hiện có' }} · {{ preview.subject.is_published ? 'Đã xuất bản' : 'Nháp' }}</span></div>
        <div class="transfer-totals"><div v-for="[label, count] in totals" :key="label"><strong>{{ count.toLocaleString('vi-VN') }}</strong><span>{{ label }}</span></div></div>
        <div class="transfer-table-wrap"><table><caption>Các thay đổi sẽ áp dụng</caption><thead><tr><th scope="col">Nội dung</th><th scope="col">Tạo mới</th><th scope="col">Cập nhật</th><th scope="col">Giữ nguyên</th></tr></thead><tbody><tr v-for="row in changes" :key="row.label"><th scope="row">{{ row.label }}</th><td>{{ row.created }}</td><td>{{ row.updated }}</td><td>{{ row.unchanged }}</td></tr></tbody></table></div>
        <p>Các bài và đề không có trong gói vẫn được giữ. Câu hỏi được sửa có phiên bản mới; bài làm cũ tiếp tục dùng câu hỏi cũ.</p>
        <div class="transfer-apply"><span>Xem lại đúng môn và các thay đổi trước khi nhập. Sao lưu DB trước khi cập nhật dữ liệu thật.</span><button type="button" class="btn btn-primary" :disabled="busy" @click="importFile">{{ importStage === 'apply' ? 'Đang nhập…' : 'Nhập học liệu này' }}</button></div>
      </section>
    </div>
  </section>
</template>

<style scoped>
.preview-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; }
.preview-heading .chip { flex-shrink: 0; }
.content-transfer { margin-top: 22px; padding: 24px; }
h2 { margin: 0; font-size: 21px; }
h3 { margin: 0 0 10px; font-size: 16px; }
p { color: #68718d; font-size: 13px; line-height: 1.7; margin: 0 0 16px; }
.transfer-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.transfer-block { padding: 20px; border: 1px solid #e7e4f4; border-radius: 16px; background: #fbfaff; min-width: 0; }
.form-control { font-size: 13px; }
input[type=file] { display: block; width: 100%; min-width: 0; font-size: 12px; padding: 10px 0; }
input[type=file]::file-selector-button { border: 1px solid #d9d3ef; border-radius: 8px; background: #fff; color: #5b51aa; padding: 9px; margin-right: 8px; cursor: pointer; }
.transfer-enrollment { display: flex; align-items: flex-start; gap: 9px; margin: 12px 0 18px; font-size: 13px; line-height: 1.5; }
.transfer-enrollment input { margin-top: 4px; }
.transfer-enrollment small { display: block; margin-top: 3px; color: #858ba0; font-size: 11px; }
.transfer-success { margin: 12px 0; padding: 13px 16px; border: 1px solid #bcdfce; border-radius: 12px; background: #edf8f0; color: #276448; }
.transfer-preview { margin-top: 20px; padding: 20px; border: 1px solid #d4cbee; border-radius: 16px; }
.transfer-preview:focus-visible { outline: 2px solid #7061bd; outline-offset: 3px; }
.transfer-totals { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; margin: 16px 0; }
.transfer-totals > div { padding: 12px; background: #f7f5fd; border-radius: 10px; }
.transfer-totals strong, .transfer-totals span { display: block; }
.transfer-totals strong { font-size: 21px; color: #51459b; margin-bottom: 5px; }
.transfer-totals span { font-size: 11px; color: #777e99; }
.transfer-table-wrap { overflow-x: auto; margin-bottom: 16px; }
table { border-collapse: collapse; width: 100%; text-align: left; font-size: 13px; }
caption { text-align: left; padding: 0 0 10px; font-weight: 700; }
th, td { padding: 10px; border-bottom: 1px solid #ece9f4; }
thead th { background: #f9f8fc; font-size: 12px; white-space: nowrap; }
tbody th { font-weight: 500; }
.transfer-apply { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.transfer-apply > span { max-width: 580px; font-size: 12px; color: #7b7890; line-height: 1.7; }
.transfer-apply .btn { flex-shrink: 0; }
@media (max-width: 800px) {
  .transfer-grid { grid-template-columns: minmax(0, 1fr); }
  .preview-heading, .transfer-apply { flex-direction: column; }
  .transfer-apply .btn { width: 100%; }
}
@media (max-width: 480px) {
  .content-transfer { padding: 18px 14px; }
  .transfer-block, .transfer-preview { padding: 16px 12px; }
  .transfer-totals { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
  .transfer-totals > div { padding: 9px; }
  .transfer-block > .btn { width: 100%; }
}
</style>
